import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { MoreVertical, UserCircle2, LayoutList, Sparkles, LogOut, LayoutDashboard, Ticket, Wrench, ClipboardList, Package, Timer, PackageSearch } from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { isNativeShellMode } from '../utils/nativeClientEnv';

const menuItems = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/tickets', label: 'Tickets', icon: Ticket },
  { to: '/hausmeister', label: 'Hausmeisterdienst', icon: Wrench },
  { to: '/formular-uebersicht', label: 'Ersatzteile', icon: ClipboardList },
  { to: '/shelf/actions', label: 'Zweitplatzierung', icon: Package },
  { to: '/zeiterfassung', label: 'Zeiterfassung', icon: Timer },
  { to: '/rolli', label: 'Rolli', icon: LayoutList },
  { to: '/we-check', label: 'WE-Check', icon: PackageSearch },
  { to: '/reinigung', label: 'Reinigungsplan', icon: Sparkles },
  { to: '/profil', label: 'Profil', icon: UserCircle2 },
];

export default function GuestHeader() {
  const { stores, selectedStoreId, logout } = useAuthStore();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const activeStore = stores.find((store) => store.id === selectedStoreId);
  const visibleMenuItems = isNativeShellMode()
    ? menuItems.filter((item) => item.to !== '/profil')
    : menuItems;

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    window.addEventListener('mousedown', handlePointerDown);
    return () => window.removeEventListener('mousedown', handlePointerDown);
  }, []);

  return (
    <header className="border-b border-slate-200 bg-white px-4 py-3 shadow-sm">
      <div className="mx-auto flex w-full max-w-6xl items-center justify-between gap-4">
        <div className="min-w-0">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-600">Gastmodus</div>
          <div className="truncate text-base font-semibold text-slate-900">
            {activeStore?.name || activeStore?.storeId || 'Markt'}
          </div>
        </div>

        <div ref={containerRef} className="relative">
          <button
            type="button"
            onClick={() => setOpen((prev) => !prev)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 shadow-sm hover:bg-slate-50"
            aria-label="Gastmenü öffnen"
          >
            <MoreVertical className="h-5 w-5" />
          </button>

          {open && (
            <div className="absolute right-0 top-12 z-50 w-64 rounded-2xl border border-slate-200 bg-white p-2 shadow-xl">
              <div className="space-y-1">
                {visibleMenuItems.map(({ to, label, icon: Icon }) => (
                  <Link
                    key={to}
                    to={to}
                    onClick={() => setOpen(false)}
                    className={`flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium ${
                      location.pathname === to ? 'bg-emerald-50 text-emerald-700' : 'text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    {label}
                  </Link>
                ))}
              </div>

              <div className="my-2 border-t border-slate-200" />

              <button
                type="button"
                onClick={async () => {
                  setOpen(false);
                  await logout();
                  navigate('/login', { replace: true });
                }}
                className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-rose-700 hover:bg-rose-50"
              >
                <LogOut className="h-4 w-4" />
                Logout
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
